import { isImmutable, fromJS } from 'immutable'
import deepmerge from 'deepmerge'

/**
 * Deep merges two objects with array override behavior, returning an Immutable object.
 *
 * This function was created as an alternative to Immutable.js's mergeDeep function,
 * which concatenates arrays during the merge process. Instead, this function performs
 * a deep merge where arrays in the source object completely replace arrays in the
 * destination object (rather than concatenating or merging array elements).
 * Both input parameters are converted to Immutable objects if they aren't already, and
 * the result is returned as an Immutable object.
 *
 * @function deepMergeOverrideArrays
 * @param {Object|Immutable.Map|Immutable.List} dest - The destination object to merge into.
 *   Can be a plain JavaScript object/array or an Immutable collection.
 * @param {Object|Immutable.Map|Immutable.List} src - The source object to merge from.
 *   Can be a plain JavaScript object/array or an Immutable collection.
 * @returns {Immutable.Map|Immutable.List} The merged result as an Immutable collection.
 *   Arrays from the source object will completely override arrays in the destination object.
 *
 * @example
 * const dest = fromJS({ users: ['alice', 'bob'], config: { theme: 'dark' } });
 * const src = { users: ['charlie'], config: { language: 'en' } };
 * const result = deepMergeOverrideArrays(dest, src);
 * // Result: Immutable({ users: ['charlie'], config: { theme: 'dark', language: 'en' } })
 *
 * @requires immutable - Uses isImmutable and fromJS from the Immutable.js library
 * @requires deepmerge - Uses the deepmerge library for object merging logic
 */
export const deepMergeOverrideArrays = (dest, src) => {
  if (!isImmutable(dest)) {
    dest = fromJS(dest)
  }

  if (!isImmutable(src)) {
    src = fromJS(src)
  }

  const merged = deepmerge(dest.toJS(), src.toJS(), {
    arrayMerge: (destArray, srcArray) => srcArray,
  })

  return fromJS(merged)
}
